import { postTablesManagementConfiguration } from '@ipfabric/api-client/client/model/configuration/configuration';
import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import type {
  NodeId,
  NodeSn,
  SnapshotId,
  TableRequestBody,
  TableResponseBody,
} from '@ipfabric/utils';
import { brand } from '@ipfabric/utils';
import type { CloudInstanceDetailData } from 'components/DeviceView/CloudConstructView/CloudInstanceDetailContent';
import type { CloudInternetGatewayDetailData } from 'components/DeviceView/CloudConstructView/CloudInternetGatewayDetailContent';
import type { CloudNatGatewayDetailData } from 'components/DeviceView/CloudConstructView/CloudNatGatewayDetailContent';
import type { CloudNetworkDetailData } from 'components/DeviceView/CloudConstructView/CloudNetworkDetailContent';
import type { CloudNetworkInterfaceDetailData } from 'components/DeviceView/CloudConstructView/CloudNetworkInterfaceDetailContent';
import type { CloudPeeringDetailData } from 'components/DeviceView/CloudConstructView/CloudPeeringDetailContent';
import type { CloudSubnetDetailData } from 'components/DeviceView/CloudConstructView/CloudSubnetDetailContent';
import { getCloudInternetGatewayDetailRequestPayload } from 'components/DeviceView/CloudConstructView/getCloudInternetGatewayDetailRequestPayload';
import { getCloudNatGatewayDetailRequestPayload } from 'components/DeviceView/CloudConstructView/getCloudNatGatewayDetailRequestPayload';
import { getCloudNetworkDetailRequestPayload } from 'components/DeviceView/CloudConstructView/getCloudNetworkDetailRequestPayload';
import type {
  DeviceViewNodeEntity,
  DeviceViewNodeId,
} from 'components/DeviceView/deviceViewTypes';
import {
  type DeviceDataForDeviceViewSettings,
  getDeviceDetailRequestPayload,
} from 'containers/HostnameDetail/getDeviceDetailRequestPayload';
import { validateMandatoryColumns } from 'utils/table/validateMandatoryColumns';
import {
  type CloudConstructKind,
  getCloudConstructKindFromNode,
} from '../../helpers/cloudConstruct';
import { hasNodeDetailTab } from '../../helpers/hasNodeDetailTab';
import type { DeviceViewCommandFactory } from '../DeviceViewModel';
import { loadDeviceViewSettings } from './loadDeviceViewSettings';
import { setTabData } from './setTabData';

const getPropertyFromExtra = (
  extra: DeviceViewNodeEntity['extra'],
  key: string,
): string | null => {
  const value = extra?.[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
};

const buildCloudNetworkInterfaceDetailFromNode = (
  entity: DeviceViewNodeEntity,
): CloudNetworkInterfaceDetailData => ({
  cloudProvider: getPropertyFromExtra(entity.extra, 'vendor'),
  networkInterfaceId: entity.sn || null,
  cloudNetworkId: getPropertyFromExtra(entity.extra, 'cloudNetworkId'),
  subnet: getPropertyFromExtra(entity.extra, 'subnetId'),
  description: getPropertyFromExtra(entity.extra, 'description'),
  availabilityZone: getPropertyFromExtra(entity.extra, 'availabilityZone'),
});

const buildCloudSubnetDetailFromNode = (
  entity: DeviceViewNodeEntity,
): CloudSubnetDetailData => ({
  cloudProvider: getPropertyFromExtra(entity.extra, 'vendor'),
  subnetId: entity.sn || null,
  cloudNetworkId: getPropertyFromExtra(entity.extra, 'cloudNetworkId'),
  name: getPropertyFromExtra(entity.extra, 'cloudConstructName'),
  availabilityZone: getPropertyFromExtra(entity.extra, 'availabilityZone'),
  region: getPropertyFromExtra(entity.extra, 'region'),
});

const buildCloudPeeringDetailFromNode = (
  entity: DeviceViewNodeEntity,
): CloudPeeringDetailData => ({
  cloudProvider: getPropertyFromExtra(entity.extra, 'vendor'),
  peeringId: entity.sn || null,
  cloudNetworkId: getPropertyFromExtra(entity.extra, 'cloudNetworkId'),
  remoteNetworkId: getPropertyFromExtra(entity.extra, 'remoteCloudNetworkId'),
  name: getPropertyFromExtra(entity.extra, 'cloudConstructName'),
});

const buildCloudInstanceDetailFromNode = (
  entity: DeviceViewNodeEntity,
): CloudInstanceDetailData => ({
  cloudProvider: getPropertyFromExtra(entity.extra, 'vendor'),
  vmId: entity.sn || null,
  name: getPropertyFromExtra(entity.extra, 'vmName'),
  status: getPropertyFromExtra(entity.extra, 'vmStatus'),
  os: getPropertyFromExtra(entity.extra, 'vmOsType'),
  image: getPropertyFromExtra(entity.extra, 'vmImage'),
  availabilityZone: getPropertyFromExtra(entity.extra, 'availabilityZone'),
});

export interface LoadDeviceViewDeviceSettingsCommand {
  deviceViewEntityId: DeviceViewNodeId;
  isPathLookupNode: boolean;
  snapshotId: SnapshotId;
}

/** Shape returned by the `get*DetailRequestPayload` builders. */
interface TableDetailRequest<T> {
  url: string;
  method: string;
  data: TableRequestBody<T>;
}

/** The tab data keys a table-backed cloud construct can fill in. */
type TabDataForCloudConstruct =
  | { cloudNetworkData: CloudNetworkDetailData | undefined }
  | { cloudNatGatewayData: CloudNatGatewayDetailData | undefined }
  | { cloudInternetGatewayData: CloudInternetGatewayDetailData | undefined };

export const loadDeviceViewDeviceSettings: DeviceViewCommandFactory<
  LoadDeviceViewDeviceSettingsCommand
> = (cmdContext, dispatch) => {
  // Constructs whose detail fields aren't carried in the node's `extra` all read
  // a single row from their own table, so only the request payload, the tab data
  // key and the error message differ between them.
  const loadCloudConstructDetail = async <T,>(
    request: TableDetailRequest<T>,
    errorMessage: string,
  ): Promise<T | undefined> => {
    try {
      const response = await cmdContext.api.request<
        TableResponseBody<T>,
        TableRequestBody<T>
      >(request);
      return response.data[0];
    } catch {
      stumble(errorMessage);
      return undefined;
    }
  };

  const loadConfiguration = async (sn: NodeSn) => {
    try {
      return validateMandatoryColumns(
        ['hash', 'hostname', 'lastChangeAt', 'sn'],
        await postTablesManagementConfiguration({
          columns: ['hash', 'hostname', 'lastChangeAt', 'sn'],
          pagination: { limit: 15, start: 0 },
          filters: { sn: ['eq', sn] },
          sort: { column: 'lastChangeAt', order: 'desc' },
        }),
      );
    } catch {
      stumble('Failed to load device configuration');
      return undefined;
    }
  };

  const handleTableBackedCloudConstructNode = async <T,>(
    command: LoadDeviceViewDeviceSettingsCommand,
    request: TableDetailRequest<T>,
    errorMessage: string,
    toTabData: (detail: T | undefined) => TabDataForCloudConstruct,
  ) => {
    const [detail] = await Promise.all([
      loadCloudConstructDetail(request, errorMessage),
      !cmdContext.getState().settings.nodesLoaded &&
        dispatch(loadDeviceViewSettings),
    ]);
    dispatch(setTabData, {
      deviceViewEntityId: command.deviceViewEntityId,
      ...toTabData(detail),
    });
  };

  const handleOtherCloudConstructNode = async (
    command: LoadDeviceViewDeviceSettingsCommand,
    entity: DeviceViewNodeEntity,
    kind: Exclude<
      CloudConstructKind,
      'cloudNetwork' | 'natGateway' | 'internetGateway'
    >,
  ) => {
    if (!cmdContext.getState().settings.nodesLoaded) {
      await dispatch(loadDeviceViewSettings);
    }
    dispatch(setTabData, {
      deviceViewEntityId: command.deviceViewEntityId,
      cloudNetworkInterfaceData:
        kind === 'networkInterface'
          ? buildCloudNetworkInterfaceDetailFromNode(entity)
          : undefined,
      cloudSubnetData:
        kind === 'subnet' ? buildCloudSubnetDetailFromNode(entity) : undefined,
      cloudPeeringData:
        kind === 'peering'
          ? buildCloudPeeringDetailFromNode(entity)
          : undefined,
      cloudInstanceData:
        kind === 'instance'
          ? buildCloudInstanceDetailFromNode(entity)
          : undefined,
    });
  };

  const handleDeviceNode = async (
    command: LoadDeviceViewDeviceSettingsCommand,
    deviceViewEntity: DeviceViewNodeEntity,
  ) => {
    const [devices, configurations] = await Promise.all([
      // If nodeType === null, entity was opened from Table and doesn't have devieType yet.
      deviceViewEntity.nodeType === null ||
      hasNodeDetailTab(deviceViewEntity.nodeType)
        ? cmdContext.api.request<
            TableResponseBody<DeviceDataForDeviceViewSettings>,
            TableRequestBody<DeviceDataForDeviceViewSettings>
          >(
            getDeviceDetailRequestPayload({
              sn: deviceViewEntity.sn,
              snapshotId: command.snapshotId,
            }),
          )
        : undefined,
      loadConfiguration(deviceViewEntity.sn),
      !cmdContext.getState().settings.nodesLoaded &&
        dispatch(loadDeviceViewSettings),
    ]);
    const device = devices?.data[0];
    const configuration = configurations?.data[0];

    // nodeType was not know when entity was initialized, we must set it to display tabs
    if (deviceViewEntity.nodeType === null && device?.devType) {
      cmdContext.updateState((draft) => {
        const foundEntity = draft.deviceViewEntities.find(
          (entity) => entity.id === command.deviceViewEntityId,
        );
        if (foundEntity?.deviceViewElement === 'node') {
          // Every record in vDevice collection has 'devType'.
          // Some devices can be of 2 device types. This is also displayed in graphs as 2 devices in 1 graph node.
          // In this case device type from 'icon' attribute has higher priority.
          // Graph response already sends value from 'icon' in 'devType'.
          // But 'devices' table sends both attributes and correct device type selection must be done on client.
          foundEntity.nodeType = device.icon ?? device.devType;
        }
      });
    }

    if (device?.id) {
      const inventoryDeviceId = device.id;
      cmdContext.updateState((draft) => {
        const foundEntity = draft.deviceViewEntities.find(
          (entity) => entity.id === command.deviceViewEntityId,
        );
        if (
          foundEntity?.deviceViewElement === 'node' &&
          foundEntity.deviceId === undefined
        ) {
          foundEntity.deviceId = brand<NodeId>(inventoryDeviceId);
        }
      });
    }

    const updatedEntity = cmdContext
      .getState()
      .deviceViewEntities.find(
        (entity) => entity.id === command.deviceViewEntityId,
      );
    if (
      !device &&
      !command.isPathLookupNode &&
      updatedEntity?.deviceViewElement === 'node' &&
      hasNodeDetailTab(updatedEntity.nodeType)
    ) {
      cmdContext.showNotification({
        type: 'info',
        message: cmdContext.msg.deviceView.deviceDoesntExist,
      });
    }

    dispatch(setTabData, {
      deviceViewEntityId: command.deviceViewEntityId,
      deviceData: device,
      configurationData: configuration,
    });
  };

  return async (command) => {
    const deviceViewEntity = cmdContext
      .getState()
      .deviceViewEntities.find(
        (entity) => entity.id === command.deviceViewEntityId,
      );
    if (deviceViewEntity?.deviceViewElement !== 'node') {
      return;
    }

    const cloudKind = getCloudConstructKindFromNode(
      deviceViewEntity.nodeType,
      deviceViewEntity.extra,
    );
    if (cloudKind === 'cloudNetwork') {
      await handleTableBackedCloudConstructNode(
        command,
        getCloudNetworkDetailRequestPayload({
          networkId: deviceViewEntity.sn,
          snapshotId: command.snapshotId,
        }),
        'Failed to load cloud network detail',
        (cloudNetworkData) => ({ cloudNetworkData }),
      );
      return;
    }
    if (cloudKind === 'natGateway') {
      await handleTableBackedCloudConstructNode(
        command,
        getCloudNatGatewayDetailRequestPayload({
          gatewayId: deviceViewEntity.sn,
          snapshotId: command.snapshotId,
        }),
        'Failed to load cloud NAT gateway detail',
        (cloudNatGatewayData) => ({ cloudNatGatewayData }),
      );
      return;
    }
    if (cloudKind === 'internetGateway') {
      await handleTableBackedCloudConstructNode(
        command,
        getCloudInternetGatewayDetailRequestPayload({
          gatewayId: deviceViewEntity.sn,
          snapshotId: command.snapshotId,
        }),
        'Failed to load cloud internet gateway detail',
        (cloudInternetGatewayData) => ({ cloudInternetGatewayData }),
      );
      return;
    }
    if (cloudKind !== null) {
      await handleOtherCloudConstructNode(command, deviceViewEntity, cloudKind);
      return;
    }

    await handleDeviceNode(command, deviceViewEntity);
  };
};
